import { makeValuesPredicate } from '@sgme/fp';
import { Products } from 'components/discover/publications/products';
import { Thematics } from 'components/discover/publications/thematics';
import { Videos } from 'components/discover/publications/videos';
import { EssentialPromoteCard } from 'components/promote-card/essential-promote-card';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { FC, ReactNode, useContext } from 'react';
import { useIntl } from 'react-intl';
import type { Region } from 'state/discover/page/page.model';
import type { SgBsTheme } from 'state/userPreferences/userPreferences.model';
import styled from 'styled-components';
import { mediaQueryDown, mediaQueryEqualOrUpper } from 'utils/mediaQueries';

import { GenericErrorBoundary } from '../common-form/builder/form/GenericErrorBoundary';
import { RegionContext } from './contexts/RegionContext';
import { PostTrade } from './post-trade';
import { CompactRegionSelector } from './regionSelector/CompactRegionSelector';
import { Research } from './research';
import { SgNews } from './sgNews/SgNews';
import { MainTopTrades, TopTrades } from './top-trades';

const countriesWithTopTradesMainDisplay = ['France'] satisfies Region[];

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  & > * {
    margin-top: var(--spacing-3);

    &:first-child {
      margin-top: 0;
    }
  }
`;

const Grid = styled.div`
  ${mediaQueryEqualOrUpper('xl')`
  &:has(#thematics) {
    --rows-count: 2;
  }

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(var(--bs-rows-count, 1), 20rem);
  column-gap: var(--spacing-4);
  row-gap: var(--spacing-4);

  #thematics {
    grid-column-start: 2;
    grid-column-end: span 1;
    grid-row-start: 1;
    grid-row-end: span 1;
  }

  #videos {
    grid-column-start: 2;
    grid-column-end: span 1;
    grid-row-end: span 1;
    &:has(+#thematics) {
      grid-row-start: 2;
    }
  }

  #products {
    grid-column-start: 1;
    grid-column-end: span 1;
    grid-row-start: 1;

    &:has(+ #thematics) {
      grid-row-end: span 2;
    }
  }
 `}
  ${mediaQueryDown('xl')`
    display:flex;
    flex-direction: column;
    padding-top: var(--spacing-3);

    #products, #videos, #thematics {
      height: 20rem;
      margin-bottom: var(--spacing-3);
    }
  `}
  ${mediaQueryEqualOrUpper('xl')`
    #products, #videos, #thematics {
      margin-bottom: 0rem;
    }
    `}
`;

interface DiscoverGridProps {
  children?: ReactNode;
  className?: string;

  theme: SgBsTheme;
  hasGivenConsentToVideo: boolean;
  onNewQuote: () => void;
}

const MinimumHeightDiv = styled.div`
  min-height: 6.5rem;
`;

const StyledRegionSelector = styled(CompactRegionSelector)`
  max-width: fit-content !important;
  min-width: fit-content !important;
`;

const isCountryTopTradesMainDisplay = makeValuesPredicate(countriesWithTopTradesMainDisplay);
export const DiscoverGrid: FC<DiscoverGridProps> = ({ onNewQuote, children, hasGivenConsentToVideo, theme }) => {
  const { formatMessage } = useIntl();
  const { region, onRegionChanged, availableRegions } = useContext(RegionContext);

  const isTopTradesMainDisplay = isCountryTopTradesMainDisplay(region);

  const canChangeWelcomePageCountry = useIsInternalUser();

  return (
    <>
      <div className="d-flex flex-column">
        <MinimumHeightDiv className="d-flex align-items-end pt-0 mt-md-4 pb-4">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8 mt-0">
                <h1 className="m-0 display-3">
                  {formatMessage({ id: 'discover.page.title', defaultMessage: 'Discover' })}
                  {children}
                </h1>
              </div>
              <div className="col-md-4 d-none d-md-flex align-items-center justify-content-end align-items-stretch">
                {canChangeWelcomePageCountry && (
                  <StyledRegionSelector value={region} onChange={onRegionChanged} availableValues={availableRegions} />
                )}
                <button
                  className="btn btn-icon-end btn-xl btn-primary text-nowrap"
                  data-e2e="welcome-page-to-new-static-screen"
                  onClick={onNewQuote}
                >
                  {formatMessage({ id: 'discover.button.newQuote', defaultMessage: 'Quote workspace' })}
                  <i className="icon">open_in_new</i>
                </button>
              </div>
            </div>
          </div>
        </MinimumHeightDiv>
        <div className="container">
          <hr className="mb-4 mt-0 border" />
          <div className="row flex-column-reverse flex-lg-row ">
            <div className="col-12 col-lg-7 col-xl-8">
              <div className="row">
                <div className="col-md-8 mb-4">
                  <h1 className="m-0 pt-1">
                    {formatMessage({ id: 'discover.title' })}
                    {children}
                  </h1>
                </div>
              </div>
              <Grid className="grid">
                <GenericErrorBoundary>
                  <Products enableSeeMore theme={theme} />
                </GenericErrorBoundary>
                <GenericErrorBoundary>
                  <Thematics enableSeeMore theme={theme} />
                </GenericErrorBoundary>
                <GenericErrorBoundary>
                  <Videos hasGivenConsent={hasGivenConsentToVideo} enableSeeMore theme={theme} />
                </GenericErrorBoundary>
              </Grid>
              {isTopTradesMainDisplay && (
                <GenericErrorBoundary>
                  <MainTopTrades />
                </GenericErrorBoundary>
              )}
              <GenericErrorBoundary>
                <SgNews />
              </GenericErrorBoundary>
              <GenericErrorBoundary>
                <Research theme={theme} />
              </GenericErrorBoundary>
            </div>
            <div className="col-12 col-lg-5 col-xl-4">
              <SidePanel>
                <EssentialPromoteCard variant="large" />
                <GenericErrorBoundary>
                  <PostTrade />
                </GenericErrorBoundary>
                {!isTopTradesMainDisplay && (
                  <GenericErrorBoundary>
                    <TopTrades />
                  </GenericErrorBoundary>
                )}
              </SidePanel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
DiscoverGrid.displayName = 'DiscoverGrid';
